// src/portal-app/shell/kit/Sparkline.js
//
// Inline area sparkline. Deliberately hand-drawn SVG rather than a chart
// library — the app ships no charting dependency and these are decorative
// trend indicators, not readable charts (the reports use real axes).
import React, { useId } from 'react';
import { ensureKitCss } from './kitCss';

const W = 300;   // viewBox width; the svg scales to its container
const PAD = 2;   // keeps the 2.5px stroke from clipping at the edges

export function Sparkline({ points, height = 56, ariaLabel, stroke = 'var(--c-green)' }) {
    // Barrel-exported like every other component, so it must inject the
    // sheet itself rather than relying on a caller having done it first —
    // it happens to be fully inline-styled today, but that is not a
    // guarantee a future consumer should have to know about.
    ensureKitCss();
    // useId must run before any early return — hooks cannot be conditional.
    // It also guarantees the gradient id is unique per instance; deriving it
    // from the data would collide whenever two sparklines share a series.
    const uid = useId();
    if (!Array.isArray(points) || points.length < 2) return null;

    const max = Math.max(...points);
    const min = Math.min(...points);
    // Flat series would divide by zero; force a mid-height line instead.
    const span = max - min || 1;
    const h = height;

    const coords = points.map((p, i) => {
        const x = PAD + (i * (W - PAD * 2)) / (points.length - 1);
        const y = PAD + (h - PAD * 2) * (1 - (p - min) / span);
        return [x, y];
    });

    const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
    const area = `${line} L${(W - PAD).toFixed(2)},${h} L${PAD},${h} Z`;
    // useId returns a value containing ':' which is invalid in a url(#…)
    // reference, so strip it.
    const gid = `pk-spark-${uid.replace(/:/g, '')}`;

    return (
        <svg
            className="pk-spark"
            viewBox={`0 0 ${W} ${h}`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: h, display: 'block' }}
            role="img"
            aria-label={ariaLabel || undefined}
            aria-hidden={ariaLabel ? undefined : 'true'}
        >
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
                    <stop offset="100%" stopColor={stroke} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={area} fill={`url(#${gid})`} stroke="none" />
            <path
                d={line}
                fill="none"
                stroke={stroke}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    );
}

export default Sparkline;
